import { motion } from "framer-motion";
import { Wrench, AlertTriangle, SearchCheck, PencilRuler } from "lucide-react";

const TIMELINE = [
  { year: "2024", icon: Wrench, cls: "bg-zinc-100 text-zinc-600", text: "Mantenimiento general" },
  { year: "2025", icon: AlertTriangle, cls: "bg-rose-50 text-rose-500", text: "Avería en sensor de cierre" },
  { year: "2025", icon: PencilRuler, cls: "bg-violet-50 text-violet-600", text: "Modificación del molde" },
  { year: "2026", icon: SearchCheck, cls: "bg-emerald-50 text-emerald-600", text: "Inspección semestral" },
];

const CHANGES = ["Operarios", "Técnicos", "Responsables", "Proveedores", "Instalaciones", "Propietarios"];

export default function Memory() {
  return (
    <section data-testid="memoria-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-36">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">Memoria permanente</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight">
              Las personas cambian. La historia de la máquina permanece.
            </h2>
            <div className="space-y-5 text-base sm:text-lg text-zinc-600 leading-relaxed mt-8 max-w-xl">
              <p>
                Una máquina industrial puede permanecer muchos años en una fábrica. Durante ese
                tiempo pueden cambiar operarios, técnicos, responsables de mantenimiento,
                proveedores, instalaciones e incluso propietarios.
              </p>
              <p>
                Sin embargo, demasiado conocimiento sobre la máquina vive únicamente en esas
                personas.
              </p>
              <p>
                Cada mantenimiento, avería, reparación, inspección o modificación se incorpora
                cronológicamente a su historial. Dentro de varios años, otra persona podrá
                entender qué ocurrió, cuándo ocurrió, qué se hizo y quién lo registró.
              </p>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10 border-l-2 border-zinc-900 pl-6 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900"
              data-testid="memoria-highlight-quote"
            >
              El conocimiento deja de depender de quién estaba allí.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="bg-white rounded-3xl border border-zinc-200/90 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.15)] p-7 sm:p-9"
          >
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-6">Historial · Inyectora 01</p>
            <div className="relative pl-1">
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-zinc-200" />
              <div className="space-y-7">
                {TIMELINE.map((t, i) => (
                  <motion.div
                    key={t.year + t.text}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.25 }}
                    className="relative flex items-center gap-4"
                  >
                    <span className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${t.cls}`}>
                      <t.icon size={16} />
                    </span>
                    <div>
                      <p className="text-xs text-zinc-400">{t.year}</p>
                      <p className="text-sm font-medium text-zinc-800 mt-0.5">{t.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-zinc-100 flex flex-wrap gap-2">
              {CHANGES.map((c) => (
                <span key={c} className="px-3 py-1.5 rounded-full bg-zinc-100 text-xs text-zinc-500">
                  {c}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
